import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { store } from "@/store/store";
import { OperationResult, ReportStep, SetReportDateResponse } from "@/models/graphql-global-types";
import { graphqlClient } from "@/utils/graphql";
import { PrintReport, PrintReportVariables } from "@/models/queries/types/PrintReport";
import { REPORT_PRINT_PDF_QUERY } from "@/models/queries/ReportPrintPDFQuery";
import { saveMeasurements, saveMeasurementsVariables } from "@/models/mutations/types/saveMeasurements";
import { SAVE_MEASURMENTS_MUTATION } from "@/models/mutations/SaveMeasurements";
import { QUERY_CHECK_REPORT_NUMBER } from "@/models/queries/CheckReportNumberQuery";
import { CheckReportNumber, CheckReportNumberVariables } from "@/models/queries/types/CheckReportNumber";
import { EditReportInfo, EditReportInfoVariables } from "@/models/mutations/types/EditReportInfo";
import { EDIT_REPORT_INFO_MUTATION } from "@/models/mutations/EditReportInfoMutation";
import { EditReportSheetInfo, EditReportSheetInfoVariables } from "@/models/mutations/types/EditReportSheetInfo";
import { EDIT_REPORT_SHEET_INFO_MUTATION } from "@/models/mutations/EditReportSheetInfoMutation";
import { EditWoodInfoMutation, EditWoodInfoMutationVariables } from "@/models/mutations/types/EditWoodInfoMutation";
import { EDIT_WOOD_INFO_MUTATION } from "@/models/mutations/EditWoodInfoMutation";
import {
  CompleteTestingOfReport,
  CompleteTestingOfReportVariables
} from "@/models/mutations/types/CompleteTestingOfReport";
import { COMPLETE_TESTING_OF_REPORT } from "@/models/mutations/CompleteTestingOfReport";
import { CompleteReport, CompleteReportVariables } from "@/models/mutations/types/CompleteReport";
import { COMPLETE_REPORT_MUTATION } from "@/models/mutations/CompleteReportMutation";
import { SetReportDate, SetReportDateVariables } from "@/models/mutations/types/SetReportDate";
import moment from "moment";
import { SET_REPORT_DATE_MUTATION } from "@/models/mutations/SetReportDate";
import { QUERY_REPORT_OVERVIEW, ReportOverview, ReportOverviewQueryResult } from "@/models/queries/ReportOverviewQuery";

import { CHANGE_REPORT_STEP_MUTATION } from "@/models/mutations/ChangeReportStepMutation";

import { ReportFilterStore } from "./ReportFilterStore";
import lodash from "lodash";
import { DELETE_REPORT_MUTUATION } from "@/models/mutations/DeleteReportMutation";
import {
  PRINT_PDF_SETTINGS_QUERY,
  PrintPdfSettings,
  PrintPdfSettingsResult
} from "@/models/queries/PrintPdfSettingsQuery";
import { sortASC } from "@/utils/sort";
import { SendReport, SendReportVariables } from "@/models/mutations/types/SendReport";
import { SEND_REPORT_MUTATION } from "@/models/mutations/SendReport";

export interface INewReportStoreState {
  hasError: boolean;
  loading: boolean;
  reports: ReportOverview[];
}

const getSortIndex = (step: ReportStep): number => {
  switch (step) {
    case ReportStep.CREATED:
      return 0;
    case ReportStep.IN_PROGRESS:
      return 1;
    case ReportStep.DONE_GATHERING:
      return 2;
    case ReportStep.COMPLETED:
      return 3;
  }
};

const sortReportOverview = (a: ReportOverview, b: ReportOverview) => {
  const sortIndexA = getSortIndex(a.step);
  const sortIndexB = getSortIndex(b.step);
  if (sortIndexA === sortIndexB) {
    return moment(a.created_at).unix() - moment(b.created_at).unix();
  }

  return sortIndexA - sortIndexB;
};

@Module({ dynamic: true, store, name: "newReportStore" })
class NewReport extends VuexModule implements INewReportStoreState {
  public reports: ReportOverview[] = [];
  public hasError: boolean = false;
  public loading: boolean = false;
  public initialLoadDone: boolean = false;
  public isLoadingSettings: boolean = false;
  public printPdfSettings?: PrintPdfSettings | null = null;

  get customersForReports() {
    const customers = NewReportStore.reports.flatMap(r => (r.sheets ? r.sheets.flatMap(s => s.product.customer) : []));
    return lodash.uniq(customers).sort(sortASC);
  }

  @Mutation
  public clearReportStore() {
    this.reports = [];
    this.initialLoadDone = false;
    this.hasError = false;
    this.loading = false;
    ReportFilterStore.clearReportFilter();
  }

  @Mutation
  private START_FETCHING_OVERVIEW() {
    this.hasError = false;
    this.loading = true;
  }

  @Mutation
  private DID_FETCH_OVERVIEW(reports: ReportOverview[]) {
    this.initialLoadDone = true;
    this.loading = false;
    this.reports = reports.sort(sortReportOverview);
    ReportFilterStore.applyFilter();
  }

  @Mutation
  private ERROR_FETCHING_OVERVIEW() {
    this.loading = false;
    this.reports = [];
    this.hasError = true;
  }

  @Mutation
  private DID_FETCH_PRINT_PDF_SETTINGS(settings: PrintPdfSettings) {
    this.printPdfSettings = { ...settings };
  }

  @Mutation
  private IS_LOADING_SETTINGS(isLoading: boolean) {
    this.isLoadingSettings = isLoading;
  }

  @Action
  public changeStatus(payload: any) {
    const report = payload.report;
    const status = payload.lane;
    return graphqlClient.mutate({
      mutation: CHANGE_REPORT_STEP_MUTATION,
      variables: { reportId: report.id, step: status }
    });
  }

  @Action
  public deleteReport(report: any) {
    return graphqlClient.mutate({
      mutation: DELETE_REPORT_MUTUATION,
      variables: { reportId: report.id }
    });
  }

  @Action
  public async fetchReportOverview() {
    try {
      this.START_FETCHING_OVERVIEW();
      const result = await graphqlClient.query<ReportOverviewQueryResult>({
        query: QUERY_REPORT_OVERVIEW
      });
      if (result.data && !result.errors) {
        this.DID_FETCH_OVERVIEW(result.data.reports);
      } else {
        this.ERROR_FETCHING_OVERVIEW();
      }
    } catch (e) {
      console.error(e);
      this.ERROR_FETCHING_OVERVIEW();
    }
  }

  @Action
  public async printPdfReport(variables: PrintReportVariables): Promise<string> {
    const result = await graphqlClient.mutate<PrintReport, PrintReportVariables>({
      mutation: REPORT_PRINT_PDF_QUERY,
      variables
    });

    if (!result.data || result.errors) {
      throw new Error();
    }

    return result.data?.printReport?.base64;
  }

  @Action
  public async sendPdfReport(variables: SendReportVariables): Promise<boolean> {
    const result = await graphqlClient.mutate<SendReport, SendReportVariables>({
      mutation: SEND_REPORT_MUTATION,
      variables
    });

    if (!result.data || result.errors) {
      throw new Error();
    }

    return result.data?.sendReport === "success" ? true : false;
  }

  @Action
  public async saveMeasurements(variables: saveMeasurementsVariables): Promise<boolean> {
    const result = await graphqlClient.mutate<saveMeasurements>({
      mutation: SAVE_MEASURMENTS_MUTATION,
      variables
    });

    if (!result.data || result.errors || result.data?.saveMeasurements !== "sucess") {
      throw new Error();
    }

    return true;
  }

  @Action
  public async checkReportNumber(variables: CheckReportNumberVariables): Promise<string> {
    const result = await graphqlClient.query<CheckReportNumber>({
      query: QUERY_CHECK_REPORT_NUMBER,
      variables
    });

    if (!result.data || !result.data.checkReportNumber) {
      throw new Error();
    }

    return result.data.checkReportNumber;
  }

  @Action
  public async saveReportInfo(variables: EditReportInfoVariables): Promise<OperationResult> {
    const result = await graphqlClient.mutate<EditReportInfo, EditReportInfoVariables>({
      variables,
      mutation: EDIT_REPORT_INFO_MUTATION
    });

    if (!result.data || result.errors || result.data.editReportInfo !== OperationResult.SUCCESS) {
      throw new Error();
    }

    return result.data.editReportInfo;
  }

  @Action
  public async saveReportSheetInfo(variables: EditReportSheetInfoVariables): Promise<OperationResult> {
    const result = await graphqlClient.mutate<EditReportSheetInfo, EditReportSheetInfoVariables>({
      variables,
      mutation: EDIT_REPORT_SHEET_INFO_MUTATION
    });

    if (!result.data || result.errors || result.data.editReportSheetInfo !== OperationResult.SUCCESS) {
      throw new Error();
    }

    return result.data.editReportSheetInfo;
  }

  @Action
  public async saveWoodInfo(variables: EditWoodInfoMutationVariables): Promise<OperationResult> {
    const result = await graphqlClient.mutate<EditWoodInfoMutation, EditWoodInfoMutationVariables>({
      variables,
      mutation: EDIT_WOOD_INFO_MUTATION
    });

    if (!result.data || result.errors || result.data.editReportSheetWoodInfo !== OperationResult.SUCCESS) {
      throw new Error();
    }

    return result.data.editReportSheetWoodInfo;
  }

  @Action
  public async completeTestingOfReport(variables: CompleteTestingOfReportVariables): Promise<boolean> {
    const result = await graphqlClient.mutate<CompleteTestingOfReport, CompleteTestingOfReportVariables>({
      variables,
      mutation: COMPLETE_TESTING_OF_REPORT
    });

    if (!result.data || result.errors || result.data.completeTestingOfReport !== "success") {
      throw new Error();
    }

    return true;
  }

  @Action
  public async completeReport(variables: CompleteReportVariables): Promise<boolean> {
    const result = await graphqlClient.mutate<CompleteReport, CompleteReportVariables>({
      variables,
      mutation: COMPLETE_REPORT_MUTATION
    });

    if (!result.data || result.errors || result.data.completeReport !== "success") {
      throw new Error();
    }

    return true;
  }

  @Action
  public async setReportDate(variables: Omit<SetReportDateVariables, "timestamp">): Promise<boolean> {
    const result = await graphqlClient.mutate<SetReportDate, SetReportDateVariables>({
      variables: {
        ...variables,
        timestamp: moment().format()
      },
      mutation: SET_REPORT_DATE_MUTATION
    });

    if (!result.data || result.errors || result.data.setReportDate !== SetReportDateResponse.SUCCESS) {
      throw new Error();
    }

    return true;
  }

  @Action
  private async loadPrintPdfSettings() {
    const result = await graphqlClient.query<PrintPdfSettingsResult>({
      query: PRINT_PDF_SETTINGS_QUERY
    });
    if (result && result.data && !result.errors) {
      this.DID_FETCH_PRINT_PDF_SETTINGS(result.data.currentPrintPdfSettings);
    }
  }

  @Action
  public async getPrintPdfSettings() {
    if (!this.printPdfSettings) {
      await this.loadPrintPdfSettings();
    }

    return this.printPdfSettings;
  }
}

export const NewReportStore = getModule(NewReport);
